import { useQuery } from "@tanstack/react-query";
import apiFetch from "../../utils/apiFetch";
import { authHeaders } from "./helpers";
import type { Employee, PaySchedule, PayRun, PD7AData } from "./types";

export interface OverviewData {
  empData: Employee[];
  schedule: PaySchedule;
  finalizedRun: PayRun | null;
}

const defaultSchedule = (): PaySchedule => ({
  frequency: "bi_weekly",
  anchorPayDate: new Date().toISOString().slice(0, 10),
  name: "Bi-weekly",
});

export function useDraftsCount() {
  return useQuery({
    queryKey: ["payroll-drafts-count"],
    queryFn: async (): Promise<PayRun[]> => {
      const r = await apiFetch("/api/v1/payroll/runs?status=draft", {
        headers: authHeaders(),
      });
      if (!r.ok) return [];
      const data = await r.json();
      return Array.isArray(data) ? data : [];
    },
    refetchOnWindowFocus: false,
  });
}

export function useOverviewData() {
  return useQuery<OverviewData>({
    queryKey: ["payroll-overview"],
    queryFn: async () => {
      const [empRes, scheduleRes, runsRes] = await Promise.all([
        apiFetch("/api/v1/payroll/employees", { headers: authHeaders() }),
        apiFetch("/api/v1/payroll/settings", { headers: authHeaders() }).catch(
          () => null
        ),
        apiFetch("/api/v1/payroll/runs", { headers: authHeaders() }).catch(
          () => null
        ),
      ]);

      let empData: Employee[] = [];
      if (empRes && empRes.ok) {
        const raw = await empRes.json();
        empData = Array.isArray(raw)
          ? raw
          : raw.items || raw.data || [];
      }

      let schedule: PaySchedule = defaultSchedule();
      if (scheduleRes && scheduleRes.ok) {
        const settings = await scheduleRes.json();
        schedule =
          settings.pay_schedule ||
          settings.paySchedule ||
          defaultSchedule();
      }

      let finalizedRun: PayRun | null = null;
      if (runsRes && runsRes.ok) {
        const runs = await runsRes.json();
        const runArr: PayRun[] = Array.isArray(runs)
          ? runs
          : runs.items || runs.data || [];
        const finalized = runArr.filter(
          (r) =>
            r.status === "approved" ||
            r.status === "paid" ||
            r.status === "finalized"
        );
        if (finalized.length > 0) finalizedRun = finalized[0];
      }

      return { empData, schedule, finalizedRun };
    },
    refetchOnWindowFocus: false,
  });
}

export function usePd7aData() {
  return useQuery<PD7AData | null>({
    queryKey: ["payroll-pd7a-current"],
    queryFn: async () => {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1;
      const resp = await apiFetch(
        "/api/v1/payroll/taxes/pd7a?year=" + year + "&month=" + month,
        { headers: authHeaders() }
      );
      if (!resp.ok) return null;
      const data = await resp.json();
      data._year = year;
      data._month = month;
      return data as PD7AData;
    },
    refetchOnWindowFocus: false,
  });
}